import Button from '@/common/components/Button'
import Checkbox from '@/common/components/Checkbox'
import { CurrencyTextField } from '@/common/components/CurrencyTextField'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import Select from '@/common/components/Select'
import SelectController from '@/common/components/SelectController'
import { useProductCategories } from '@/common/hooks/queries/useProductCategories'
import useDebounce from '@/common/hooks/useDebounce'
import { Mapper } from '@/common/services/mapper'
import { ProductCategoryData } from '@/common/types/productCategory.type'
import { formatCurrency } from '@/common/utils/formatters'
import { FormTransformers } from '@/common/utils/formTransformers'
import { ContractProductCategoryItem } from '@/features/privateCompanies/components/updateContract/UpdateCreditLimitsModal/ContractProductCategoryItem'
import { useUpdateContractCreditLimits } from '@/features/privateCompanies/hooks/contract/useUpdateContractCreditLimits'
import {
  UpdateContractCreditLimitsSchema,
  updateContractCreditLimitsSchema,
} from '@/features/privateCompanies/schemas/contract/updateCreditLimits.schema'
import { contractProductCategoriesDefaultValues } from '@/features/privateCompanies/services/contractProductCategoriesDefaultValues'
import { CompanyContractType } from '@/features/privateCompanies/types/companyContract.type'
import { getPriceRuleOptions } from '@/features/publicCompanyLeadsFunnel/constants/priceRuleOptions.constant'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { SearchIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Controller, FormProvider, SubmitHandler, useFieldArray, useForm } from 'react-hook-form'

interface UpdateCreditLimitsModalProps {
  contract?: CompanyContractType
  companyKind?: string
}

export const UpdateCreditLimitsModal = ({ contract, companyKind }: UpdateCreditLimitsModalProps) => {
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<string[] | boolean>(false)
  const priceRuleOptions = useMemo(() => getPriceRuleOptions({ isPrivateFuel: true }), [])

  const [searchProductCategory, setSearchProductCategory] = useDebounce('')

  const { data: productCategoryOptions } = useProductCategories({
    filters: { search: searchProductCategory },
    mapper: Mapper.mapToOptionsWithItem<ProductCategoryData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const formMethods = useForm<UpdateContractCreditLimitsSchema>({
    resolver: zodResolver(updateContractCreditLimitsSchema),
  })

  const {
    control,
    register,
    handleSubmit,
    setError,
    setValue,
    reset,
    formState: { errors },
  } = formMethods

  const {
    fields: contractProductCategories,
    append: appendContractProductCategory,
    remove: removeContractProductCategory,
  } = useFieldArray({
    control,
    name: `contract.contract_product_categories_attributes`,
  })

  const handleAppendContractProductCategory = (productCategory: ProductCategoryData) => {
    appendContractProductCategory({
      product_category: productCategory,
    })
  }

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['clientContract', contract?.id] })
    setIsOpen(false)
  }

  const { mutate: updateContractCreditLimits, isPending: updateContractCreditLimitsIsPending } =
    useUpdateContractCreditLimits({
      contractId: contract?.id,
      companyKind,
      isConsumptionControlledByQuantity: contract?.consumption_controlled_by === 'quantity',
      contractProductCategories: contract?.contract_product_categories || [],
      onSuccess,
      onError: FormTransformers.errorsTransformer<UpdateContractCreditLimitsSchema>({ setError, setMessages }),
    })

  const onSubmit: SubmitHandler<UpdateContractCreditLimitsSchema> = (data) => {
    updateContractCreditLimits(data)
  }

  useEffect(() => {
    if (isOpen) {
      reset({
        contract: {
          granted_credit:
            companyKind === 'public_company'
              ? contract?.global_value
                ? formatCurrency(Number(contract.global_value))
                : ''
              : contract?.granted_credit
                ? formatCurrency(Number(contract.granted_credit))
                : '',
          default_fuel_price_rule: priceRuleOptions.find(
            (option) => option.value === contract?.default_fuel_price_rule,
          ),
          require_product_credit_quota: !!contract?.require_product_credit_quota,
          contract_product_categories_attributes: contract?.contract_product_categories?.map(
            (contractProductCategory) => contractProductCategoriesDefaultValues({ contractProductCategory }) || [],
          ),
          is_consumption_controlled_by_quantity: contract?.consumption_controlled_by === 'quantity',
        },
      })
    }
  }, [isOpen])

  useEffect(() => {
    setValue('contract.is_consumption_controlled_by_quantity', contract?.consumption_controlled_by === 'quantity');
  }, [contract?.consumption_controlled_by]);

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onConfirm={handleSubmit(onSubmit)}
      Trigger={
        <Button
          name="open_update_credit_limits_modal"
          operationKlassToCheckPermission={'Admin::Client::Contracts::UpdateContractCreditsInformation'}
          className="h-[2.125rem] text-sm text-white-800"
        >
          Editar
        </Button>
      }
      title="Editando limites de crédito"
      dataTestid="update_credit_limits"
      bodyClassName="max-h-[calc(100vh-10rem)] overflow-y-auto"
      useCancellableFooter
      confirmButtonClassName="bg-blue-700"
      disabled={updateContractCreditLimitsIsPending}
      confirmButtonIsLoading={updateContractCreditLimitsIsPending}
      body={
        <>
          <FormProvider {...formMethods}>
            <div className="flex flex-col gap-y-4 text-white-800">
              <Controller
                name="contract.granted_credit"
                control={control}
                disabled={updateContractCreditLimitsIsPending}
                render={({ field }) => (
                  <CurrencyTextField
                    label="Valor global do contrato"
                    {...field}
                    errorMessage={errors?.contract?.granted_credit?.message}
                  />
                )}
              />

              <Checkbox
                label="Informar valores de cota"
                {...register('contract.require_product_credit_quota')}
                disabled={updateContractCreditLimitsIsPending}
              />

              <Select
                name="search_product_categories"
                label="Serviços e Produtos"
                placeholder={<SearchIcon className="size-4 text-white-800" />}
                options={
                  productCategoryOptions?.filter((productCategory) => {
                    return !contractProductCategories?.some(
                      ({ product_category }) => product_category?.id === productCategory.value,
                    )
                  }) || []
                }
                onChange={(option) =>
                  handleAppendContractProductCategory((option as { item: ProductCategoryData }).item)
                }
                onInputChange={setSearchProductCategory}
                value=""
                isDisabled={updateContractCreditLimitsIsPending}
              />

              {!!contractProductCategories?.length &&
                contractProductCategories?.map((contractProductCategory, productCategoryIndex) => (
                  <ContractProductCategoryItem
                    key={contractProductCategory.id}
                    contractProductCategory={contractProductCategory}
                    productCategoryIndex={productCategoryIndex}
                    removeContractProductCategory={removeContractProductCategory}
                    disabled={updateContractCreditLimitsIsPending}
                    contract={contract}
                  />
                ))}

              <SelectController
                control={control}
                name="contract.default_fuel_price_rule"
                label="Regra de Preço (Padrão)"
                className="mb-20"
                options={priceRuleOptions.filter((option) => option.value !== 'fixed')}
                isDisabled={updateContractCreditLimitsIsPending}
                errorMessage={errors?.contract?.default_fuel_price_rule?.message}
              />
            </div>

            {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
          </FormProvider>
        </>
      }
    />
  )
}
